import React from 'react';
import { View, ScrollView, Pressable } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useHospitalityStore } from '../store/useHospitalityStore';
import { useAppStore } from '../store/useAppStore';
import { resourceId, fmt12, inr } from '../data/format';
import {
  Card, CardHead, CardTitle, CardSub, CardBody,
  Chip, Switch, Input, Select, TimeInput, Field, Tabs, Icon, Txt, Button,
  Row, Col, Spread, Grow, Screen,
  Table, THead, TH, TR, TD, Modal,
} from '../components';
import { useTheme } from '../theme';
import type { SettingsTab } from '../store/useHospitalityStore';
import type { CombinePolicy, ServiceToggles } from '../data/seed';
import { createSection, updateSection, deactivateSection } from '../services/config/section';
import { listStaff, createStaff, type Staff } from '../services/people/staff';
import { createFloorTable, updateFloorTable, deactivateFloorTable } from '../services/config/table';
import { createLocation, updateLocation } from '../services/config/location';
import { upsertSetting } from '../services/config/setting';
import type { TableShape as ApiTableShape, UpdateFloorTableInput } from '../schemas/config/table';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

/**
 * Settings — ported from SK Hospitality.dc.html template lines 744-967 and the
 * settingsVals() view-model (lines 1643-1714). These four tables are the ONLY data
 * the adapter persists; edits sync the live floor plan and the derived resource ids.
 */

const TIMEZONES = ['Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Europe/London'];
const COMBINE_OPTIONS: { v: CombinePolicy; l: string }[] = [
  { v: 'same-section', l: 'Same section only' },
  { v: 'any-adjacent', l: 'Any adjacent tables' },
  { v: 'off', l: 'No combining' },
];
const SERVICE_META: { k: keyof ServiceToggles; label: string; desc: string }[] = [
  { k: 'autoWaitlistText', label: 'Auto-notify waitlist guests', desc: 'Text / WhatsApp a guest the moment their table frees up.' },
  { k: 'noShowHold', label: 'Hold no-show reservations', desc: 'Keep a booked table reserved 10 min past arrival time before releasing it.' },
  { k: 'communalSeating', label: 'Allow communal seating', desc: 'Let unrelated parties share one large table (e.g. the 8-seat bench).' },
  { k: 'qrSelfJoin', label: 'Guest QR self-join', desc: 'Guests scan a table-tent QR to add themselves to the waitlist.' },
];

// <select> option lists — built from the same choices the original <option> markup used.
const TIMEZONE_OPTIONS = TIMEZONES.map((tz) => ({ label: tz, value: tz }));
const SHAPE_OPTIONS = [
  { label: 'square', value: 'square' },
  { label: 'round', value: 'round' },
  { label: 'communal', value: 'communal' },
];

// app shape (square | round | communal) → adapter shape (square | circle | rectangle)
const toApiShape = (s: string): ApiTableShape => (s === 'round' ? 'circle' : s === 'communal' ? 'rectangle' : 'square');

const trashPath = 'M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13';

/** The prototype's trash glyph — not in the shared ICONS map, so rendered inline. */
function Trash({ color, size = 14 }: { color: string; size?: number }) {
  return (
    <Svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <Path d={trashPath} stroke={color} strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/** The combine-policy segmented buttons (was chipBtn CSS in the web version). */
function ChipButton({ active, label, onPress }: { active: boolean; label: string; onPress: () => void }) {
  const t = useTheme();
  return (
    <Pressable
      onPress={onPress}
      style={{
        flex: 1,
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderRadius: 9,
        borderWidth: 1,
        borderColor: active ? t.colors.accent : t.colors.line,
        backgroundColor: active ? t.colors.accentSoft : t.colors.card,
      }}
    >
      <Txt size="md" weight="medium" center color={active ? 'accentInk' : 'ink2'}>{label}</Txt>
    </Pressable>
  );
}

// Client-side validation for the venue form — mirrors the adapter's createLocationSchema
// (name min 1, timezone min 1, open/close HH:MM 24h) so we surface field errors inline
// instead of firing invalid autosaves that the backend rejects with 400.
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
type LocErrors = { name?: string; timezone?: string; open?: string; close?: string };

// serverRef is an SK People employee id. Only send it once it matches a real,
// currently-fetched employee — not by pattern-matching the id's shape (some
// legitimately real employees predate the emp_<ulid> convention and still
// carry a plain UUID from before that migration, e.g. dev seed data created
// under the old DB-default id column). Checking membership in the list we
// just fetched from SK People is the actual source of truth; a regex on id
// shape was silently dropping serverRef for those employees with zero error.
const validRef = (ref: string | undefined, staffList: Staff[]): string | undefined =>
  ref && staffList.some((st) => st.id === ref) ? ref : undefined;
function validateLocation(loc: { name: string; timezone: string; open: string; close: string }): LocErrors {
  const e: LocErrors = {};
  if (!loc.name.trim()) e.name = 'Venue name is required';
  if (!loc.timezone) e.timezone = 'Pick a timezone';
  if (!HHMM.test(loc.open)) e.open = 'Use HH:MM (24-hour)';
  if (!HHMM.test(loc.close)) e.close = 'Use HH:MM (24-hour)';
  return e;
}

export default function Settings() {
  const t = useTheme();
  const s = useHospitalityStore();
  const { settingsTab: st, location: loc, sections, tables, settings: set } = s;
  // The platform org name (e.g. "Usher Technology"), not the per-venue
  // hosp_location config — loc.org is only ever hydrated as a side-effect of
  // loading an EXISTING venue, so it stays blank until the first venue is
  // created even when the org name itself is already known.
  const orgName = useAppStore((state) => state.orgName);

  // Venue-form validation + save state. Errors show inline after a save attempt.
  const [locErrors, setLocErrors] = React.useState<LocErrors>({});
  const [savingLoc, setSavingLoc] = React.useState(false);

  // SK People staff for the section server picker (fetched via backbone).
  const [staff, setStaff] = React.useState<Staff[]>([]);
  const loadStaff = React.useCallback(() => {
    listStaff().then(setStaff).catch(() => setStaff([]));
  }, []);
  React.useEffect(() => { loadStaff(); }, [loadStaff]);

  // "+ Add server" modal — creates a real SK People employee (name-only).
  const ADD_SERVER = '__add_server__';
  const [addServerFor, setAddServerFor] = React.useState<string | null>(null); // section id awaiting a new server
  const [newServerName, setNewServerName] = React.useState('');
  const [savingServer, setSavingServer] = React.useState(false);

  const tabs = [
    { id: 'venue', label: 'Venue' }, { id: 'sections', label: 'Sections' }, { id: 'tables', label: 'Floor tables' },
    { id: 'timing', label: 'Timing & turns' }, { id: 'policies', label: 'Policies' },
  ];

  const firstSec = sections[0];
  const firstLabel = tables.find((tb) => tb.section === firstSec?.name)?.label || 'T1';
  const resourceExample = resourceId(loc.org, loc.name, firstSec?.name || '', firstLabel);

  const tm = set.timing;
  const start = 15 * 60;
  const smallEnd = start + tm.diningSmall + tm.cleaningBuffer + tm.bookingGap;
  const groupEnd = start + tm.diningGroup + tm.cleaningBuffer + tm.bookingGap;

  // ── service-layer wiring ──
  // Every handler does the optimistic local store mutation first (so the prototype
  // UI updates even with the backend down), then best-effort persists to the adapter
  // and toasts the real backend message. locationId comes from the live venue; when
  // there's no persisted venue yet we skip the network call and keep the local flow.
  const locationId = s.apiLocationId;

  // Save the venue location — PATCH the live row, or POST a new one if none exists yet.
  // Explicit-save (button), not autosave: validate client-side first so we never fire an
  // invalid request; surface field errors inline instead of a backend 400 toast.
  const handleSaveLocation = async () => {
    const g = useHospitalityStore.getState().location;
    const errs = validateLocation(g);
    setLocErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSavingLoc(true);
    try {
      const payload = { name: g.name, timezone: g.timezone, operatingOpen: g.open, operatingClose: g.close };
      const res = g.apiId
        ? await updateLocation(g.apiId, payload)
        : await createLocation({ ...payload, locationId: useAppStore.getState().platformLocationId });
      // On first create, capture the persisted id: store it as the venue's apiId
      // (so re-saving PATCHes instead of POSTing a duplicate) and mark the backend
      // live so apiLocationId is set. Without this, sections/tables/settings all
      // hit `if (!locationId) return` and stay local-only until a page reload.
      if (!g.apiId && res?.id) {
        s.hydrateConfig({ location: { apiId: res.id } });
        s.markBackendLive(res.id);
      }
      showSuccess(res, g.apiId ? 'Venue updated' : 'Venue created');
    } catch (err) {
      handleApiError(err, 'Save failed');
    } finally {
      setSavingLoc(false);
    }
  };

  // Add a section — local insert, then create the adapter row and store its apiId so
  // later renames PATCH the row instead of erroring. serverRef only sent when a valid uuid.
  const handleAddSection = async () => {
    s.addSection();
    if (!locationId) return;
    const created = useHospitalityStore.getState().sections.at(-1);
    if (!created) return;
    try {
      const res = await createSection({ locationId, name: created.name, serverRef: validRef(created.serverRef, staff) });
      if (res?.id) s.setSectionApiId(created.id, res.id);
      showSuccess(res, 'Section added');
    } catch (err) {
      handleApiError(err, 'Add section failed');
    }
  };

  // Assign a section's server. `value` is an SK People employee id, or the
  // ADD_SERVER sentinel (open the create-server modal). Stores serverRef = id +
  // the display name, then PATCHes the section row.
  // Persist an inline section rename on blur (PATCH the row). Local store already
  // updated via setSectionName's onChangeText; this saves it to the adapter.
  const handleSectionNameBlur = async (secId: string, apiId: string | undefined) => {
    if (!apiId) return;
    const sec = useHospitalityStore.getState().sections.find((x) => x.id === secId);
    if (!sec?.name.trim()) return;
    try {
      const res = await updateSection(apiId, { name: sec.name, serverRef: validRef(sec.serverRef, staff) });
      showSuccess(res, 'Section renamed');
    } catch (err) {
      handleApiError(err, 'Rename section failed');
    }
  };

  const handleSectionServer = async (secId: string, apiId: string | undefined, value: string) => {
    if (value === ADD_SERVER) { setAddServerFor(secId); return; }
    const picked = staff.find((st) => st.id === value);
    if (!picked) return;
    s.setSectionServerRef(secId, picked.id, picked.name);
    if (!apiId) return;
    try {
      const sec = useHospitalityStore.getState().sections.find((x) => x.id === secId);
      const res = await updateSection(apiId, { name: sec?.name, serverRef: validRef(sec?.serverRef, staff) });
      showSuccess(res, 'Section updated');
    } catch (err) {
      handleApiError(err, 'Update section failed');
    }
  };

  // Create a new server as an SK People employee (name-only), then assign it to
  // the section that opened the modal. Refreshes the picker so it appears for all.
  const handleCreateServer = async () => {
    const secId = addServerFor;
    const name = newServerName.trim();
    if (!secId || !name) return;
    const [firstName, ...rest] = name.split(/\s+/);
    setSavingServer(true);
    try {
      const orgName = useHospitalityStore.getState().location.org;
      const created = await createStaff(firstName, rest.join(' '), orgName);
      setStaff((prev) => [{ id: created.id, name: created.name }, ...prev]);
      const sec = useHospitalityStore.getState().sections.find((x) => x.id === secId);
      s.setSectionServerRef(secId, created.id, created.name);
      if (sec?.apiId) {
        const res = await updateSection(sec.apiId, { name: sec.name, serverRef: created.id });
        showSuccess(res, 'Server added · SK People');
      } else {
        showSuccess(undefined, 'Server added · SK People');
      }
      setAddServerFor(null);
      setNewServerName('');
    } catch (err) {
      handleApiError(err, 'Add server failed');
    } finally {
      setSavingServer(false);
    }
  };

  // Remove a section — local removal (guarded for tables in-store), then deactivate the row.
  const handleRemoveSection = async (secId: string, apiId: string | undefined) => {
    const before = useHospitalityStore.getState().sections.some((x) => x.id === secId);
    s.removeSection(secId);
    const removed = before && !useHospitalityStore.getState().sections.some((x) => x.id === secId);
    if (!removed || !apiId) return; // store blocked it (section still has tables) or never persisted
    try {
      await deactivateSection(apiId);
      showSuccess(undefined, 'Section removed');
    } catch (err) {
      handleApiError(err, 'Remove section failed');
    }
  };

  // Add a floor table — local insert, then create the adapter row. The adapter
  // provisions the table's SK Calendar `place` calendar and returns its id; we do
  // NOT send calendarResourceId (it was a slug that never matched a cal ULID).
  // Capture the created row's apiId so later edits PATCH instead of no-op.
  const handleAddTable = async (sectionName: string) => {
    s.addTable(sectionName);
    if (!locationId) return;
    const created = useHospitalityStore.getState().tables.at(-1);
    if (!created) return;
    try {
      const sec = useHospitalityStore.getState().sections.find((x) => x.name === sectionName);
      const res = await createFloorTable({
        locationId,
        sectionId: sec?.apiId,
        label: created.label,
        seats: created.seats,
        shape: toApiShape(created.shape),
        combinable: created.combinable !== false,
        communal: !!created.communal,
        posX: created.x,
        posY: created.y,
      });
      if (res?.id) s.setTableApiId(created.id, res.id);
      showSuccess(res, 'Table added');
    } catch (err) {
      handleApiError(err, 'Add table failed');
    }
  };

  // Edit a discrete table field (shape / combinable / communal) — local update, then PATCH the row.
  const handleTableField = async (tbId: string, apiId: string | undefined, key: 'shape' | 'combinable' | 'communal', value: string | boolean) => {
    s.setTableField(tbId, key, value);
    if (!apiId) return;
    try {
      const patch: UpdateFloorTableInput =
        key === 'shape' ? { shape: toApiShape(value as string) } : { [key]: value as boolean };
      const res = await updateFloorTable(apiId, patch);
      showSuccess(res, 'Table updated');
    } catch (err) {
      handleApiError(err, 'Update table failed');
    }
  };

  // Persist label/seats on blur (text inputs update the local store on each keystroke
  // via setTableField; this PATCHes the row once editing ends). label uppercased/≤5;
  // seats is a positive int.
  const handleTableTextBlur = async (tbId: string, apiId: string | undefined) => {
    // seats is left at 0 while the field is mid-edit (see onChangeText below,
    // which no longer force-defaults to 1 on every keystroke) — clamp to a
    // real minimum now that the operator is done typing.
    if (useHospitalityStore.getState().tables.find((x) => x.id === tbId)?.seats === 0) {
      s.setTableField(tbId, 'seats', 1);
    }
    if (!apiId) return;
    const tb = useHospitalityStore.getState().tables.find((x) => x.id === tbId);
    if (!tb || !tb.label.trim()) return;
    try {
      const res = await updateFloorTable(apiId, { label: tb.label, seats: tb.seats });
      showSuccess(res, 'Table updated');
    } catch (err) {
      handleApiError(err, 'Update table failed');
    }
  };

  // Remove a floor table — local removal (guarded for occupied tables), then deactivate the row.
  const handleRemoveTable = async (tbId: string, apiId: string | undefined) => {
    const before = useHospitalityStore.getState().tables.some((x) => x.id === tbId);
    s.removeTable(tbId);
    const removed = before && !useHospitalityStore.getState().tables.some((x) => x.id === tbId);
    if (!removed || !apiId) return; // store blocked it (occupied) or never persisted
    try {
      await deactivateFloorTable(apiId);
      showSuccess(undefined, 'Table removed');
    } catch (err) {
      handleApiError(err, 'Remove table failed');
    }
  };

  // Combine policy — local update, then upsert the hosp_setting row.
  const handleCombinePolicy = async (v: CombinePolicy) => {
    s.setCombinePolicy(v);
    if (!locationId) return;
    try {
      const res = await upsertSetting({ locationId, key: 'combine_policy', value: v });
      showSuccess(res, 'Policy saved');
    } catch (err) {
      handleApiError(err, 'Save failed');
    }
  };

  // Service automation toggle — local update, then upsert the tier3_toggles setting.
  const handleToggleService = async (k: keyof ServiceToggles, on: boolean) => {
    s.toggleService(k, on);
    if (!locationId) return;
    try {
      const g = useHospitalityStore.getState().settings;
      const res = await upsertSetting({ locationId, key: 'tier3_toggles', value: { service: g.service } });
      showSuccess(res, 'Setting saved');
    } catch (err) {
      handleApiError(err, 'Save failed');
    }
  };

  // Turn times — persist on blur to its own 'turn_times' setting. The adapter reads
  // this to derive each booking's hold window (dining + cleaning buffer + gap).
  const handleSaveTurnTimes = async () => {
    if (!locationId) return;
    try {
      const res = await upsertSetting({ locationId, key: 'turn_times', value: useHospitalityStore.getState().settings.timing });
      showSuccess(res, 'Turn times saved');
    } catch (err) {
      handleApiError(err, 'Save failed');
    }
  };

  // Upsell rule add / toggle / remove — local update, then upsert the upsell_rule_ref array.
  const pushUpsellRules = async () => {
    if (!locationId) return;
    try {
      const g = useHospitalityStore.getState().settings;
      const res = await upsertSetting({ locationId, key: 'upsell_rule_ref', value: g.upsellRules });
      showSuccess(res, 'Upsell rules saved');
    } catch (err) {
      handleApiError(err, 'Save failed');
    }
  };
  const handleAddUpsellRule = () => { s.addUpsellRule(); void pushUpsellRules(); };
  const handleToggleUpsellRule = (id: string, on: boolean) => { s.toggleUpsellRule(id, on); void pushUpsellRules(); };
  const handleRemoveUpsellRule = (id: string) => { s.removeUpsellRule(id); void pushUpsellRules(); };

  return (
    <Screen style={{ maxWidth: 1060, width: '100%' }}>
      {/* Config-only banner */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingVertical: 12, paddingHorizontal: 14, backgroundColor: t.colors.accentSoft, borderWidth: 1, borderColor: t.colors.accentLine, borderRadius: 10 }}>
        <Icon name="info" size={16} color={t.colors.accent} strokeWidth={1.7} style={{ marginTop: 1 }} />
        <Txt size="sm" color="accentInk" style={{ flex: 1, lineHeight: 18 }}>
          <Txt size="sm" weight="bold" color="accentInk">Config only. </Txt>
          These four tables (<Txt size="sm" mono color="accentInk">hosp_location · hosp_section · hosp_floor_table · hosp_setting</Txt>) are the only data the adapter persists — all of it tenant-isolated by row-level security. Tables, bills and reservations live by reference in SK Calendar / Catalog / Billing.
        </Txt>
      </View>

      <Tabs tabs={tabs} activeId={st} onChange={(id) => s.setSettingsTab(id as SettingsTab)} />

      {/* VENUE */}
      {st === 'venue' && (
        <Col gap={16}>
          <Card>
            <CardHead>
              <View><CardTitle>Location</CardTitle><CardSub>hosp_location</CardSub></View>
              <Chip variant="neutral" dot>RLS · tenant-isolated</Chip>
            </CardHead>
            <CardBody style={{ gap: 12 }}>
              <Field label="Organisation" hint="Set on the platform — not editable here."><Input value={orgName || loc.org} editable={false} /></Field>
              <Field label="Venue name" error={locErrors.name}><Input value={loc.name} onChangeText={(v) => { s.setLocation('name', v); if (locErrors.name) setLocErrors((e) => ({ ...e, name: undefined })); }} /></Field>
              <Field label="Timezone" error={locErrors.timezone}>
                <Select value={loc.timezone} onValueChange={(v) => { s.setLocation('timezone', v); if (locErrors.timezone) setLocErrors((e) => ({ ...e, timezone: undefined })); }} options={TIMEZONE_OPTIONS} />
              </Field>
              <Row gap={14}>
                <Grow><Field label="Opens" error={locErrors.open}><TimeInput value={loc.open} onChangeText={(v) => { s.setLocation('open', v); if (locErrors.open) setLocErrors((e) => ({ ...e, open: undefined })); }} /></Field></Grow>
                <Grow><Field label="Closes" error={locErrors.close}><TimeInput value={loc.close} onChangeText={(v) => { s.setLocation('close', v); if (locErrors.close) setLocErrors((e) => ({ ...e, close: undefined })); }} /></Field></Grow>
              </Row>
              <Row>
                <Grow />
                <Button onPress={() => void handleSaveLocation()} disabled={savingLoc}>
                  {savingLoc ? 'Saving…' : loc.apiId ? 'Save changes' : 'Create venue'}
                </Button>
              </Row>
              <View style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-start', paddingVertical: 11, paddingHorizontal: 12, backgroundColor: t.colors.paper2, borderRadius: 9 }}>
                <Icon name="info" size={15} color={t.colors.ink4} strokeWidth={1.7} style={{ marginTop: 1 }} />
                <Txt size="sm" color="ink3" style={{ flex: 1, lineHeight: 18 }}>
                  Calendar resource IDs follow <Txt size="sm" mono color="ink2">org_location_section_table</Txt> — e.g. <Txt size="sm" mono color="accent">{resourceExample}</Txt>. Rename the venue and every table's ref updates; the org segment comes from the platform.
                </Txt>
              </View>
            </CardBody>
          </Card>
          <Card>
            <CardHead><View><CardTitle>What this controls</CardTitle></View></CardHead>
            <CardBody>
              <Txt size="md" color="ink2" style={{ lineHeight: 21 }}>
                Operating hours gate which reservation slots SK Calendar will offer. The timezone stamps every booking and WhatsApp confirmation. No back-dating is allowed, and nothing here is ever hard-deleted — rows are deactivated.
              </Txt>
            </CardBody>
          </Card>
        </Col>
      )}

      {/* SECTIONS */}
      {st === 'sections' && (
        <Card>
          <CardHead>
            <View><CardTitle>Sections</CardTitle><CardSub>hosp_section · rename inline · server is a reference into SK People</CardSub></View>
            <Button size="sm" onPress={() => void handleAddSection()}>
              <Icon name="plus" size={13} color={t.colors.ink} strokeWidth={2} /><Txt size="sm" weight="medium" color="ink">Add section</Txt>
            </Button>
          </CardHead>
          <View style={{ padding: 16 }}>
            <Table>
              <THead>
                <TH flex={2}>Section name</TH>
                <TH flex={2}>Server (SK People)</TH>
                <TH flex={2}>Ref</TH>
                <TH flex={1} style={{ alignItems: 'flex-end' }}>Tables</TH>
                <TH flex={1} style={{ alignItems: 'flex-end' }}>Seats</TH>
                <TH width={44} />
              </THead>
              {sections.map((sec, i) => {
                const tbs = tables.filter((tb) => tb.section === sec.name);
                return (
                  <TR key={sec.id} last={i === sections.length - 1}>
                    <TD flex={2}><Input size="sm" style={{ maxWidth: 180 }} value={sec.name} onChangeText={(v) => s.setSectionName(sec.id, v)} onBlur={() => void handleSectionNameBlur(sec.id, sec.apiId)} /></TD>
                    <TD flex={2}><Select size="sm" style={{ maxWidth: 170 }} value={sec.serverRef} onValueChange={(v) => void handleSectionServer(sec.id, sec.apiId, v)} options={[...staff.map((st) => ({ label: st.name, value: st.id })), { label: '+ Add server', value: ADD_SERVER }]} placeholder="Assign server" /></TD>
                    <TD flex={2}><Txt size="xs" mono color="ink4">{sec.serverRef || '—'}</Txt></TD>
                    <TD flex={1} style={{ alignItems: 'flex-end' }}><Txt mono>{String(tbs.length)}</Txt></TD>
                    <TD flex={1} style={{ alignItems: 'flex-end' }}><Txt mono>{String(tbs.reduce((a, tb) => a + tb.seats, 0))}</Txt></TD>
                    <TD width={44} style={{ alignItems: 'flex-end' }}>
                      <Button variant="ghost" icon size="xs" onPress={() => void handleRemoveSection(sec.id, sec.apiId)}><Trash color={t.colors.ink3} /></Button>
                    </TD>
                  </TR>
                );
              })}
            </Table>
          </View>
        </Card>
      )}

      {/* + ADD SERVER modal — creates a real SK People employee (name-only). */}
      {addServerFor && (
        <Modal
          title="Add server"
          onClose={() => { setAddServerFor(null); setNewServerName(''); }}
          size="sm"
          footer={
            <>
              <Button variant="ghost" size="sm" onPress={() => { setAddServerFor(null); setNewServerName(''); }}>
                <Txt size="sm" weight="medium" color="ink2">Cancel</Txt>
              </Button>
              <Button size="sm" disabled={savingServer || !newServerName.trim()} onPress={() => void handleCreateServer()}>
                <Txt size="sm" weight="medium" color="ink">{savingServer ? 'Adding…' : 'Add server'}</Txt>
              </Button>
            </>
          }
        >
          <Col gap={12}>
            <Field label="Full name" hint="Creates an employee in SK People — they become assignable across the venue.">
              <Input value={newServerName} onChangeText={setNewServerName} placeholder="e.g. Anjali Verma" autoFocus />
            </Field>
          </Col>
        </Modal>
      )}

      {/* FLOOR TABLES */}
      {st === 'tables' && (
        <Col gap={14}>
          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-start', paddingVertical: 11, paddingHorizontal: 14, backgroundColor: t.colors.paper2, borderWidth: 1, borderColor: t.colors.line, borderRadius: 10 }}>
            <Icon name="info" size={15} color={t.colors.ink4} strokeWidth={1.7} style={{ marginTop: 1 }} />
            <Txt size="sm" color="ink3" style={{ flex: 1, lineHeight: 19 }}>
              Tables are grouped by section. <Txt size="sm" weight="bold" color="ink2">Combinable</Txt> = this table may be joined with a neighbour for a larger party. <Txt size="sm" weight="bold" color="ink2">Communal</Txt> = unrelated parties can share it (e.g. a long bench). Edits sync the live floor plan instantly.
            </Txt>
          </View>
          {sections.map((sec) => (
            <Card key={sec.id}>
              <CardHead>
                <Row gap={9}><CardTitle>{sec.name}</CardTitle><Chip variant="neutral">{sec.prefix}</Chip></Row>
                <Button size="sm" onPress={() => void handleAddTable(sec.name)}>
                  <Icon name="plus" size={13} color={t.colors.ink} strokeWidth={2} /><Txt size="sm" weight="medium" color="ink">Add table</Txt>
                </Button>
              </CardHead>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ padding: 16 }}>
                <View style={{ minWidth: 720 }}>
                  <Table>
                    <THead>
                      <TH width={80}>Label</TH>
                      <TH width={72}>Seats</TH>
                      <TH width={118}>Shape</TH>
                      <TH width={96}>Combinable</TH>
                      <TH width={90}>Communal</TH>
                      <TH width={210}>Calendar resource</TH>
                      <TH width={44} />
                    </THead>
                    {tables.filter((tb) => tb.section === sec.name && !tb.mergedInto).map((tb, i, arr) => (
                      <TR key={tb.id} last={i === arr.length - 1}>
                        <TD width={80}><Input size="sm" style={{ width: 72 }} value={tb.label} onChangeText={(v) => s.setTableField(tb.id, 'label', v.toUpperCase().slice(0, 5))} onBlur={() => void handleTableTextBlur(tb.id, tb.apiId)} /></TD>
                        <TD width={72}><Input size="sm" style={{ width: 62 }} keyboardType="number-pad" value={tb.seats === 0 ? '' : String(tb.seats)} onChangeText={(v) => { const n = parseInt(v, 10); s.setTableField(tb.id, 'seats', v.trim() === '' ? 0 : Number.isNaN(n) ? 0 : Math.max(0, n)); }} onBlur={() => void handleTableTextBlur(tb.id, tb.apiId)} /></TD>
                        <TD width={118}><Select size="sm" style={{ width: 108 }} value={tb.shape} onValueChange={(v) => void handleTableField(tb.id, tb.apiId, 'shape', v)} options={SHAPE_OPTIONS} /></TD>
                        <TD width={96}><Switch checked={tb.combinable !== false} onChange={(v) => void handleTableField(tb.id, tb.apiId, 'combinable', v)} /></TD>
                        <TD width={90}><Switch checked={!!tb.communal} onChange={(v) => void handleTableField(tb.id, tb.apiId, 'communal', v)} /></TD>
                        <TD width={210}><Txt size="xs" mono color="ink4">{resourceId(loc.org, loc.name, sec.name, tb.label)}</Txt></TD>
                        <TD width={44} style={{ alignItems: 'flex-end' }}>
                          <Button variant="ghost" icon size="xs" onPress={() => void handleRemoveTable(tb.id, tb.apiId)}><Trash color={t.colors.ink3} /></Button>
                        </TD>
                      </TR>
                    ))}
                  </Table>
                </View>
              </ScrollView>
            </Card>
          ))}
        </Col>
      )}

      {/* TIMING & TURNS */}
      {st === 'timing' && (
        <Col gap={16}>
          <Card>
            <CardHead><View><CardTitle>Turn times</CardTitle><CardSub>hosp_setting · how long a table is held per booking</CardSub></View></CardHead>
            <CardBody style={{ gap: 12 }}>
              <Row gap={14}>
                <Grow><Field label="Dining · 1–2 guests (min)"><Input keyboardType="number-pad" value={String(tm.diningSmall)} onChangeText={(v) => s.setTiming('diningSmall', v)} onBlur={() => void handleSaveTurnTimes()} /></Field></Grow>
                <Grow><Field label="Dining · 3+ guests (min)"><Input keyboardType="number-pad" value={String(tm.diningGroup)} onChangeText={(v) => s.setTiming('diningGroup', v)} onBlur={() => void handleSaveTurnTimes()} /></Field></Grow>
              </Row>
              <Row gap={14}>
                <Grow><Field label="Table cleaning buffer (min)"><Input keyboardType="number-pad" value={String(tm.cleaningBuffer)} onChangeText={(v) => s.setTiming('cleaningBuffer', v)} onBlur={() => void handleSaveTurnTimes()} /></Field></Grow>
                <Grow><Field label="Extra gap between bookings (min)"><Input keyboardType="number-pad" value={String(tm.bookingGap)} onChangeText={(v) => s.setTiming('bookingGap', v)} onBlur={() => void handleSaveTurnTimes()} /></Field></Grow>
              </Row>
              <Row gap={14}>
                <Grow>
                  <Field label="Show Reserved on floor plan (min before booking)">
                    <Input keyboardType="number-pad" value={String(tm.reservedLeadMinutes)} onChangeText={(v) => s.setTiming('reservedLeadMinutes', v)} onBlur={() => void handleSaveTurnTimes()} />
                  </Field>
                </Grow>
                <Grow />
              </Row>
              <Txt size="sm" color="ink3" style={{ lineHeight: 18 }}>
                A confirmed reservation blocks its table from walk-in seating starting this many minutes before its booked time — not the whole day in advance.
              </Txt>
            </CardBody>
          </Card>
          <Card>
            <CardHead><View><CardTitle>Worked example</CardTitle><CardSub>when the next booking can take that table</CardSub></View></CardHead>
            <CardBody style={{ gap: 12 }}>
              <Row gap={12}>
                <Grow style={{ alignItems: 'center', paddingVertical: 12, paddingHorizontal: 12, borderWidth: 1, borderColor: t.colors.line, borderRadius: 10, backgroundColor: t.colors.paper2 }}>
                  <Txt size="xs" color="ink3" upper style={{ letterSpacing: 0.6 }}>Seated at</Txt>
                  <Txt mono size="xl" weight="semibold">3:00 PM</Txt>
                </Grow>
                <Icon name="arrowr" size={22} color={t.colors.ink4} strokeWidth={1.8} />
                <View style={{ flex: 1.4, gap: 8 }}>
                  <Spread style={{ paddingVertical: 9, paddingHorizontal: 12, borderWidth: 1, borderColor: t.colors.accentLine, borderRadius: 9, backgroundColor: t.colors.accentSoft }}>
                    <View><Txt size="xs" color="accentInk">Table of 2 free again</Txt><Txt mono size="lg" weight="bold" color="accent">{fmt12(smallEnd)}</Txt></View>
                    <Chip variant="accent">{`${tm.diningSmall + tm.cleaningBuffer + tm.bookingGap} min`}</Chip>
                  </Spread>
                  <Spread style={{ paddingVertical: 9, paddingHorizontal: 12, borderWidth: 1, borderColor: t.colors.line, borderRadius: 9 }}>
                    <View><Txt size="xs" color="ink3">Group of 6 free again</Txt><Txt mono size="lg" weight="bold">{fmt12(groupEnd)}</Txt></View>
                    <Chip variant="neutral">{`${tm.diningGroup + tm.cleaningBuffer + tm.bookingGap} min`}</Chip>
                  </Spread>
                </View>
              </Row>
              <Txt size="sm" color="ink3" style={{ lineHeight: 18 }}>SK Calendar uses these to decide the next bookable slot on the same table — dining time + cleaning buffer + any extra gap. No double-booking, no rushed turns.</Txt>
            </CardBody>
          </Card>
          <Card style={{ backgroundColor: '#2E2A57', borderColor: 'transparent' }}>
            <CardBody style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
              <View style={{ width: 46, height: 46, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.16)', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="agent" size={24} color="#fff" strokeWidth={1.7} />
              </View>
              <View style={{ flex: 1 }}>
                <Row gap={9} wrap>
                  <Txt size="lg" weight="semibold" color="#fff">Smart turn-time prediction</Txt>
                  <View style={{ backgroundColor: 'rgba(255,255,255,0.18)', borderRadius: 999, paddingHorizontal: 9, height: 22, justifyContent: 'center' }}>
                    <Txt size="xs" weight="medium" color="#fff">Coming soon</Txt>
                  </View>
                </Row>
                <Txt size="sm" color="#fff" style={{ opacity: 0.85, marginTop: 3, lineHeight: 18 }}>These are fixed defaults today. Next, SUKI will predict the real turn time per booking — learning from how busy the room is, the day & time, the party size, and even the specific guest's past visits — so slots tighten on quiet nights and relax for lingering regulars.</Txt>
              </View>
              <Switch checked={false} onChange={() => {}} disabled />
            </CardBody>
          </Card>
        </Col>
      )}

      {/* POLICIES */}
      {st === 'policies' && (
        <Col gap={16}>
          <Card>
            <CardHead><View><CardTitle>Combine policy</CardTitle><CardSub>hosp_setting · combine_policy</CardSub></View></CardHead>
            <CardBody>
              <Row gap={8}>
                {COMBINE_OPTIONS.map((o) => (
                  <ChipButton key={o.v} active={set.combinePolicy === o.v} label={o.l} onPress={() => void handleCombinePolicy(o.v)} />
                ))}
              </Row>
            </CardBody>
          </Card>
          <Card>
            <CardHead><View><CardTitle>Dashboard view</CardTitle><CardSub>hosp_setting · dashboard_view</CardSub></View></CardHead>
            <CardBody>
              <Spread style={{ paddingVertical: 10, paddingHorizontal: 12, borderWidth: 1, borderColor: t.colors.line, borderRadius: 9 }}>
                <View><Txt size="md" weight="medium">{set.dashboardView}</Txt><Txt size="xs" color="ink3">Fixed layout — AI-generated views arrive in a later stage</Txt></View>
                <Chip variant="neutral">locked</Chip>
              </Spread>
            </CardBody>
          </Card>
          <Card>
            <CardHead>
              <View><CardTitle>Upsell rules</CardTitle><CardSub>hosp_setting · upsell_rule_ref → SK Catalog rule IDs · editable</CardSub></View>
              <Button size="sm" onPress={() => handleAddUpsellRule()}>
                <Icon name="plus" size={13} color={t.colors.ink} strokeWidth={2} /><Txt size="sm" weight="medium" color="ink">Add rule</Txt>
              </Button>
            </CardHead>
            <CardBody style={{ gap: 12 }}>
              {set.upsellRules.map((r) => {
                const delta = r.toPrice - r.fromPrice;
                const example = `When a guest orders ${r.fromItem}${r.fromPrice ? ' (' + inr(r.fromPrice) + ')' : ''}, SUKI suggests ${r.toItem} (${inr(r.toPrice)})`;
                return (
                  <View key={r.id} style={{ borderWidth: 1, borderColor: t.colors.line, borderRadius: 11, paddingVertical: 13, paddingHorizontal: 14 }}>
                    <Spread style={{ marginBottom: 10 }}>
                      <Grow><Input size="sm" style={{ maxWidth: 260 }} value={r.name} onChangeText={(v) => s.setUpsellField(r.id, 'name', v)} /></Grow>
                      <Switch checked={r.on} onChange={(v) => handleToggleUpsellRule(r.id, v)} />
                    </Spread>
                    <Row wrap gap={10} style={{ alignItems: 'flex-end' }}>
                      <Field label="When guest orders" style={{ flex: 1, minWidth: 150 }}><Input size="sm" value={r.fromItem} onChangeText={(v) => s.setUpsellField(r.id, 'fromItem', v)} /></Field>
                      <Field label="at ₹" style={{ width: 96 }}><Input size="sm" keyboardType="number-pad" value={String(r.fromPrice)} onChangeText={(v) => s.setUpsellField(r.id, 'fromPrice', Math.max(0, parseInt(v, 10) || 0))} /></Field>
                      <Icon name="arrowr" size={18} color={t.colors.accent} strokeWidth={1.8} style={{ marginBottom: 9 }} />
                      <Field label="SUKI suggests" style={{ flex: 1, minWidth: 150 }}><Input size="sm" value={r.toItem} onChangeText={(v) => s.setUpsellField(r.id, 'toItem', v)} /></Field>
                      <Field label="at ₹" style={{ width: 96 }}><Input size="sm" keyboardType="number-pad" value={String(r.toPrice)} onChangeText={(v) => s.setUpsellField(r.id, 'toPrice', Math.max(0, parseInt(v, 10) || 0))} /></Field>
                    </Row>
                    <Spread style={{ marginTop: 11, paddingTop: 11, borderTopWidth: 1, borderTopColor: t.colors.line }}>
                      <Row gap={8} style={{ flex: 1 }}>
                        <Chip variant="accent">{delta > 0 ? '+' + inr(delta) : 'add-on'}</Chip>
                        <Txt size="sm" color="ink3" style={{ flex: 1, lineHeight: 17 }}>{example}</Txt>
                      </Row>
                      <Button variant="ghost" icon size="xs" onPress={() => handleRemoveUpsellRule(r.id)}><Trash color={t.colors.ink3} /></Button>
                    </Spread>
                  </View>
                );
              })}
            </CardBody>
          </Card>
          <Card>
            <CardHead><View><CardTitle>Service automation</CardTitle><CardSub>hosp_setting · what SUKI handles without a human</CardSub></View></CardHead>
            <CardBody>
              {SERVICE_META.map((o) => (
                <Spread key={o.k} style={{ paddingVertical: 11, borderBottomWidth: 1, borderBottomColor: t.colors.line, alignItems: 'flex-start' }}>
                  <View style={{ flex: 1, paddingRight: 14 }}>
                    <Txt size="md" weight="medium" color="ink">{o.label}</Txt>
                    <Txt size="sm" color="ink3" style={{ marginTop: 2, lineHeight: 17 }}>{o.desc}</Txt>
                  </View>
                  <Switch checked={set.service[o.k]} onChange={(v) => void handleToggleService(o.k, v)} style={{ marginTop: 2 }} />
                </Spread>
              ))}
            </CardBody>
          </Card>
        </Col>
      )}
    </Screen>
  );
}
